// The office comes alive (flightsim-96z). Every art/ cockpit was built with
// its instruments and controls as live nodes (model-integration.md 1h, the
// "Moving nodes" table under each office): needles pivoting on their dial's
// centre, a horizon bar, a compass card, the stick or column and wheel on its
// floor pivot, pedals, throttles. cockpit.js seats the pilot; this file makes
// the panel read what the airframe is doing, so a glance down at the
// airspeed in a dive shows the needle winding round, the horizon bar tips
// with the bank, and the stick leans the way the mouse is pulling it.
//
// Calibrated to the faces, not to a round number. The needles read against
// the figures each office's builder painted on its own dials (cockpitlib's
// NUMS / FIGS tables): the Spitfire's airspeed puts 200 mph at 130 degrees,
// the Bf 109's puts 400 km/h at -15 and the Fw 190's 500 km/h at +21. A dial
// table is a list of [reading, degrees] points, degrees clockwise from 12
// o'clock as the pilot sees it, interpolated between and pinned at both ends
// (a needle stops on its peg, it doesn't wind round past it). Every allied
// panel reads mph (the Corsair's knots, as the Navy flew), feet and ft/min;
// the German two read km/h, metres and m/s — and ATA for boost, where the
// Spitfire reads pounds and the Americans inches of mercury.
//
// The engine has no RPM in the flight model: the tach and boost follow the
// throttle through a spool lag, from idle to each engine's rated figures, and
// a dead engine lets them fall to a stopped prop and the air's own pressure —
// the dial a pilot checks first when the nose goes quiet.
//
// Everything eases on the WALL clock (frame()'s wallDt): an instrument's lag
// is a property of the instrument, and under 10 fps a lag integrated on the
// clamped dt would leave the needles crawling while the plane dives.
//
// The hood (flightsim-5l1). A fighter pilot taxied and came aboard with the
// hood open, for the air, the view over the nose and a quick way out if the
// deck went wrong, and shut it for the fight. So the fighters' hoods ride
// back on their rails low and slow — the rearm pass over a friendly carrier's
// deck, or rolling near the ground — and run shut again as the airframe
// climbs away: the Spitfire's, the Corsair's, the Fw 190's and the Mustang's
// bubble slide aft (the 190's and the Mustang's cranks winding with them),
// the Bf 109's swings up and over to starboard on its hinge. A hood takes a
// second and a half to travel, eased at both ends like a hand hauling it
// along the rails.

import * as THREE from "three";

const MPH = 2.2369363;
const KNOTS = 1.9438445;
const KMH = 3.6;
const FEET = 3.2808399;
const FPM = 196.85039; // ft/min per m/s
const D2R = Math.PI / 180;

// The allied rate-of-climb dial: zero at 9 o'clock, climb clockwise and
// descent anticlockwise, 30 degrees a thousand feet a minute, the two 6s
// meeting at 3 o'clock (a needle stops just short of the pin between them).
const VSI_FPM = [[-5900, -177], [5900, 177]];
// The German Variometer: the same face in metres a second.
const VSI_MS = [[-29.5, -177], [29.5, 177]];
// The turn needle: standing up at rest, the dial's ticks at +-40 marking a
// 6 deg/s turn. A fighter reefing round at 20 deg/s pins it on the peg.
const TURN = [[-8, -53], [8, 53]];

// The offices, by stem. `speed`/`alt`/`climb` name the units the faces read
// in; each dial is [reading, degrees] points (see the file header). `zero` is
// where a needle's own geometry points at rest — its degrees at the dial's
// zero mark — so a needle turns by (reading's degrees - zero). `engine` is
// the idle and rated RPM and boost the tach and manifold spool between, and
// the boost a stopped engine reads (the ambient air). `stick`: the pilot's
// stick node, or `column` (+ `wheel` riding it); `pedalsTurn` for hangers
// that swing (the Fortress) rather than slide. `compassFlat` for the P8 bowl
// whose card lies flat and turns about the vertical. `hood`: how far the
// sliding hood runs aft on its rails when open (`slide`, metres; `crank`,
// turns of the winding handle over the full run), or how far a hinged hood
// swings (`swing`, radians). The P-38 and the Fortress fly shut: the one's
// hatch lifts off, the other is a heavy's flight deck.
export const OFFICE_GAUGES = {
  // The Spitfire: Barr & Stroud blind-flying panel, mph, the Merlin's boost
  // in pounds (the dial's -3 on its peg, +12 at the top of the arc).
  spitfire_cockpit: {
    speed: MPH, alt: FEET, climb: FPM,
    airspeed: [[0, 0], [100, 40], [200, 130], [300, 220], [400, 300], [440, 330]],
    tach: [[0, -150], [6000, 150]],
    manifold: [[-3.2, -135], [0, -75], [8, 75], [12, 135], [14, 165]],
    zero: { airspeed: 0, tach: -150, manifold: -135 },
    engine: { idle: 700, rated: 3000, boostIdle: -3, boostRated: 12, ambient: 0 },
    column: "Column", compassFlat: true,
    // Back over the fixed rear glazing until its tail meets the glazing's
    // end, which leaves the bow over the pilot's head.
    hood: { slide: 0.59 },
  },
  // The P-51D: mph on a face figured in hundreds (100 at 45 degrees, then 50
  // degrees a hundred to 700 at the peg), the Merlin's inches of mercury from
  // 10 at the peg to 75, the tach figured in hundreds from 0 low on the left
  // to 35. The bubble winds back on its rails by the crank on the starboard
  // wall, until its bow rides behind the pilot's head.
  p51d_cockpit: {
    speed: MPH, alt: FEET, climb: FPM,
    airspeed: [[0, 0], [100, 45], [200, 95], [300, 145], [400, 195], [500, 245], [600, 290], [700, 330]],
    tach: [[0, -150], [3500, 150]],
    manifold: [[10, -150], [75, 150]],
    zero: { airspeed: 0, tach: -150, manifold: -150 },
    engine: { idle: 650, rated: 3000, boostIdle: 18, boostRated: 61, ambient: 29.9 },
    stick: "Stick",
    hood: { slide: 0.76, crank: 6 },
  },
  // The P-38L: mph, the Allisons' pairs on one pivot each.
  p38_cockpit: {
    speed: MPH, alt: FEET, climb: FPM,
    airspeed: [[0, 0], [500, 300], [550, 330]],
    tach: [[0, -150], [3000, 150]],
    manifold: [[15, -135], [65, 135]],
    zero: { airspeed: 0, tach: -150, manifold: -135 },
    engine: { idle: 700, rated: 3000, boostIdle: 17, boostRated: 54, ambient: 29.9 },
    column: "Column", wheel: "Wheel",
  },
  // The B-17G's left seat: mph, four Cyclones, and a copilot's column,
  // wheel and pedals ganged to ours as a real pair were.
  b17_cockpit: {
    speed: MPH, alt: FEET, climb: FPM,
    airspeed: [[0, 0], [100, 40], [500, 280], [550, 310]],
    tach: [[0, -150], [4000, 150]],
    manifold: [[15, -135], [65, 135]],
    zero: { airspeed: 0, tach: -150, manifold: -135 },
    engine: { idle: 600, rated: 2500, boostIdle: 16, boostRated: 46, ambient: 29.9 },
    column: "Column", wheel: "Wheel", copilot: true, pedalsTurn: true,
  },
  // The Bf 109 G: the drawing hangs airspeed and U/min from 6 o'clock, km/h,
  // metres, ATA; no climb dial on this panel.
  bf109_cockpit: {
    speed: KMH, alt: 1, climb: 1,
    airspeed: [[0, -180], [200, -110], [300, -60], [400, -15], [500, 30], [600, 80], [700, 130], [750, 155]],
    tach: [[0, -180], [3500, 135]],
    manifold: [[0.6, -150], [1.8, 120]],
    zero: { airspeed: -180, tach: -180, manifold: -150 },
    engine: { idle: 700, rated: 2800, boostIdle: 0.6, boostRated: 1.42, ambient: 1 },
    stick: "Stick",
    // The hood hinges on the starboard rail and swings up and over, past
    // the upright, to lie on its stay above the starboard wing.
    hood: { swing: 100 * D2R },
  },
  // The Fw 190 A-8: its figures laid on every other tick of 21.4 degrees.
  fw190_cockpit: {
    speed: KMH, alt: 1, climb: 1,
    airspeed: [[0, -180], [100, -150], [800, 150], [850, 171]],
    tach: [[0, -150], [3600, 150]],
    manifold: [[0.467, -135], [1.8, 135]],
    zero: { airspeed: -180, tach: -150, manifold: -135 },
    engine: { idle: 700, rated: 2700, boostIdle: 0.6, boostRated: 1.42, ambient: 1 },
    stick: "Stick",
    // Wound aft by the crank on the starboard wall, until the head armour
    // riding in it clears the pilot's head.
    hood: { slide: 0.6, crank: 5 },
  },
  // The F4U-1D: an unfigured Navy panel, read in knots (40 a tick), the
  // R-2800's inches of mercury 10 a tick from the peg.
  f4u_cockpit: {
    speed: KNOTS, alt: FEET, climb: FPM,
    airspeed: [[0, 0], [440, 330]],
    tach: [[0, -135], [3600, 135]],
    manifold: [[10, -135], [90, 135]],
    zero: { airspeed: 0, tach: -135, manifold: -135 },
    engine: { idle: 650, rated: 2700, boostIdle: 18, boostRated: 57, ambient: 29.9 },
    stick: "Stick",
    // Aft along the turtle deck's tracks, its bow behind the pilot's head.
    hood: { slide: 0.92 },
  },
};

// How far the controls throw (radians, metres). A stick leans 14 degrees to
// the stops fore and aft and 11 side to side; a column rocks 12; a wheel
// turns 60 each way; pedals slide 5 cm or swing 12 degrees; a throttle runs
// 60 degrees from its closed stop at the back of the quadrant to full.
const STICK_PITCH = 14 * D2R;
const STICK_ROLL = 11 * D2R;
const COLUMN_PITCH = 12 * D2R;
const WHEEL_ROLL = 60 * D2R;
const PEDAL_SLIDE = 0.05;
const PEDAL_SWING = 12 * D2R;
const THROTTLE_THROW = 60 * D2R;
// The horizon bar: full travel (a third of the dial's radius, where the
// bezel starts to swallow it) at 30 degrees of pitch.
const HORIZON_PITCH = 30 * D2R;
const HORIZON_TRAVEL = 0.45; // of the bar's half-span (the bar spans 0.66 R each way)

// Instrument lags (seconds, the time constant of a first-order ease).
const LAG = {
  airspeed: 0.15, alt: 0.2, climb: 0.9, turn: 0.45, engine: 0.6,
  gyro: 0.06, compass: 0.35, controls: 0.05,
};
// Each eased reading's lag.
const LAG_OF = {
  airspeed: LAG.airspeed, alt: LAG.alt, climb: LAG.climb, turn: LAG.turn, rpm: LAG.engine, boost: LAG.engine,
  pitch: LAG.gyro, pitchCtl: LAG.controls, rollCtl: LAG.controls, yawCtl: LAG.controls, thr: LAG.controls,
};
// A frame longer than this snaps every instrument to its reading: a respawn,
// a tab switched back to, or the first frame in the seat.
const SNAP_SEC = 1;

// The hood opens low and slow: under 40 m/s within 10 m of the ground (a
// fighter's roll-out), or, over a friendly flight deck, anywhere inside the
// deck crews' rearm window (the caller passes it, config ships.resupply*:
// 55 m/s, 30 m) — a Navy pilot came aboard with the hood back, so a pass
// over the deck slides it open as the crews go to work. It shuts again 5 m/s
// faster or 10 m higher than it opened, so a pilot hanging on the edge of
// the gate doesn't set it chattering on its rails.
const HOOD_OPEN_MS = 40;
const HOOD_OPEN_AGL = 10;
const HOOD_SHUT_MS = 5; // over the opening speed
const HOOD_SHUT_AGL = 10; // over the opening height
const HOOD_SEC = 1.5; // one full run, open to shut

/**
 * Should the hood be open? `f.speed` (m/s), `f.agl` (metres above the ground
 * or the deck under the airframe) and `f.deck` ({ speed, agl }: the deck's
 * rearm window when that is a friendly carrier's deck); `open` is where it is
 * headed now, which the gate's two edges straddle. No height, no hood: a
 * flight the caller can't put over the ground flies shut.
 */
export function hoodWanted(f, open) {
  const speed = f.speed ?? 0;
  const agl = f.agl;
  if (!(agl < Infinity)) return false;
  let maxSpeed = f.deck ? f.deck.speed : HOOD_OPEN_MS;
  let maxAgl = f.deck ? f.deck.agl : HOOD_OPEN_AGL;
  if (open) {
    maxSpeed += HOOD_SHUT_MS;
    maxAgl += HOOD_SHUT_AGL;
  }
  return speed < maxSpeed && agl < maxAgl;
}

/**
 * A dial's degrees for `v` on the points table `pts` ([reading, degrees],
 * readings ascending): linear between points, pinned at both ends.
 */
export function dialDeg(pts, v) {
  if (!(v > pts[0][0])) return pts[0][1];
  for (let i = 1; i < pts.length; i++) {
    const [v1, d1] = pts[i];
    if (v <= v1) {
      const [v0, d0] = pts[i - 1];
      return d0 + ((d1 - d0) * (v - v0)) / (v1 - v0);
    }
  }
  return pts[pts.length - 1][1];
}

/** The engine's RPM and boost for a lever at `throttle` (0..1), health `engine` (0 = dead). */
export function engineReading(eng, throttle, engine) {
  if (!(engine > 0.02)) return { rpm: 0, boost: eng.ambient };
  const t = Math.min(Math.max(throttle, 0), 1);
  return {
    rpm: eng.idle + (eng.rated - eng.idle) * t,
    boost: eng.boostIdle + (eng.boostRated - eng.boostIdle) * t * (0.55 + 0.45 * engine),
  };
}

// Heading (clockwise from north, north = -Z), bank (right wing down
// positive) and pitch (nose up positive), radians, off an attitude.
const _f = new THREE.Vector3();
const _r = new THREE.Vector3();
const _u = new THREE.Vector3();
export function attitudeAngles(att, out = {}) {
  _f.set(1, 0, 0).applyQuaternion(att);
  _u.set(0, 1, 0).applyQuaternion(att);
  _r.set(0, 0, 1).applyQuaternion(att);
  out.heading = Math.atan2(_f.x, -_f.z);
  out.pitch = Math.asin(Math.max(-1, Math.min(1, _f.y)));
  out.bank = Math.atan2(-_r.y, _u.y);
  return out;
}

const wrapPi = (a) => a - 2 * Math.PI * Math.floor((a + Math.PI) / (2 * Math.PI));
const ease = (cur, want, dt, tau) => cur + (want - cur) * (1 - Math.exp(-dt / tau));

const _qa = new THREE.Quaternion();
const _qb = new THREE.Quaternion();
const X = new THREE.Vector3(1, 0, 0);
const Y = new THREE.Vector3(0, 1, 0);
const Z = new THREE.Vector3(0, 0, 1);

/**
 * One office's live nodes, driven from the flight. `nodeOf(name)` looks a
 * node up by its prefix-less part name (ModelInstance.node); `stem` picks the
 * office's dials. Nodes the office doesn't carry are skipped (the 109 has no
 * climb dial, the P-38 no stick).
 */
export class OfficeGauges {
  constructor(nodeOf, stem) {
    this.cfg = OFFICE_GAUGES[stem] ?? null;
    this.parts = new Map(); // bare name → { node, q, p } (rest pose)
    this.fresh = true;
    this.lastAtt = null; // last frame's attitude (the turn gyro's reference)
    this.s = {}; // the eased readings
    this.t = 0; // seconds in the seat (the hurt engine's shake)
    this.hoodOpen = false; // where the hood is headed
    this.hood = 0; // how far along its run it is (0 shut, 1 open)
    this._ang = {};
    if (!this.cfg) return;
    const want = [
      "Needle_Airspeed", "Needle_Altimeter", "Needle_Altimeter1k", "Needle_Climb", "Needle_Turn",
      "Needle_Tach", "Needle_Manifold", "Horizon", "Compass",
      "Stick", "Column", "Wheel", "CoColumn", "CoWheel",
      "Pedal_L", "Pedal_R", "CoPedal_L", "CoPedal_R", "Throttle", "Hood", "Crank",
    ];
    for (const s of ["_L", "_R", "_1", "_2", "_3", "_4"]) {
      want.push("Needle_Tach" + s, "Needle_Manifold" + s, "Throttle" + s);
    }
    for (const name of want) {
      const node = nodeOf(name);
      if (node) this.parts.set(name, { node, q: node.quaternion.clone(), p: node.position.clone() });
    }
    // The horizon bar's travel, from its own span.
    const h = this.parts.get("Horizon");
    if (h) {
      const box = new THREE.Box3();
      h.node.updateMatrixWorld(true);
      const inv = new THREE.Matrix4().copy(h.node.matrixWorld).invert();
      h.node.traverse((o) => {
        if (!o.isMesh) return;
        o.geometry.computeBoundingBox();
        box.union(o.geometry.boundingBox.clone().applyMatrix4(new THREE.Matrix4().multiplyMatrices(inv, o.matrixWorld)));
      });
      h.travel = Math.max(Math.abs(box.min.z), Math.abs(box.max.z)) * HORIZON_TRAVEL;
    }
  }

  /** The next update snaps every instrument to its reading (a new sortie in the seat). */
  reset() {
    this.fresh = true;
  }

  /**
   * One frame. `f` = { speed (m/s), alt (m above the sea), vy (m/s),
   * att (quaternion), ctl: { pitch, roll, yaw, throttle } (the wire's sense:
   * pitch + nose up, roll + right, yaw + LEFT rudder), engine (0..1, the
   * engine's health; 0 = dead or dry), agl (m above the ground or deck
   * under the airframe) and deck (the rearm window over a friendly deck, or
   * null): the hood's gate, hoodWanted; turnRate (optional,
   * deg/s: a staged still's turn, which has no swing of its own to read) },
   * `wallDt` = the frame's real seconds.
   */
  update(f, wallDt) {
    const cfg = this.cfg;
    if (!cfg || !f) return;
    const a = attitudeAngles(f.att, this._ang);
    const snap = this.fresh || !(wallDt < SNAP_SEC);
    const dt = snap ? 0 : Math.max(wallDt, 0);
    const s = this.s;
    // The turn needle is a rate gyro: it reads the airframe's swing about its
    // own vertical axis (the nose going right is a turn right), not the
    // compass heading's rate, which runs wild with the nose near the vertical.
    let turnRate = 0;
    if (dt > 0 && this.lastAtt) {
      _qa.copy(this.lastAtt).invert().multiply(f.att);
      turnRate = (-2 * (_qa.w < 0 ? -_qa.y : _qa.y)) / dt / D2R;
    }
    (this.lastAtt ??= new THREE.Quaternion()).copy(f.att);
    // A staged still (the screenshot director) has no swing to read: it says.
    if (f.turnRate !== undefined) turnRate = f.turnRate;
    const ctl = f.ctl ?? {};
    const eng = engineReading(cfg.engine, ctl.throttle ?? 0, f.engine ?? 1);
    const want = {
      airspeed: (f.speed ?? 0) * cfg.speed,
      alt: (f.alt ?? 0) * cfg.alt,
      climb: (f.vy ?? 0) * cfg.climb,
      turn: turnRate,
      rpm: eng.rpm,
      boost: eng.boost,
      pitch: a.pitch,
      pitchCtl: ctl.pitch ?? 0,
      rollCtl: ctl.roll ?? 0,
      yawCtl: ctl.yaw ?? 0,
      thr: ctl.throttle ?? 0,
    };
    for (const k in want) s[k] = snap || s[k] === undefined ? want[k] : ease(s[k], want[k], dt, LAG_OF[k]);
    // The bank gyro and the compass card wrap: ease along the short way round.
    const fresh = snap || s.bank === undefined;
    s.bank = fresh ? a.bank : a.bank - wrapPi(a.bank - s.bank) * Math.exp(-dt / LAG.gyro);
    s.heading = fresh ? a.heading : a.heading - wrapPi(a.heading - s.heading) * Math.exp(-dt / LAG.compass);
    // On the first frame the turn needle has no rate yet: let it rise from rest.
    if (snap) s.turn = f.turnRate ?? 0;
    // The hood: a new sortie in the seat finds it where the flight puts it;
    // after that it travels its rails at a hand's pace.
    this.hoodOpen = hoodWanted(f, this.hoodOpen);
    const hoodTo = this.hoodOpen ? 1 : 0;
    this.hood = snap ? hoodTo : this.hood + Math.max(-dt / HOOD_SEC, Math.min(dt / HOOD_SEC, hoodTo - this.hood));
    this.fresh = false;
    this.t += dt;
    // A hurt engine runs rough: the tach and boost shake about their
    // reading, harder the worse it's hit, and stop with the engine.
    const hurt = f.engine > 0.02 && f.engine < 0.7 ? (0.7 - f.engine) / 0.7 : 0;
    this.shake = hurt * Math.sin(this.t * 23) * Math.sin(this.t * 7.3);
    this._pose(cfg, s);
  }

  _needle(name, deg, zero) {
    const p = this.parts.get(name);
    if (!p) return;
    p.node.quaternion.copy(p.q).multiply(_qa.setFromAxisAngle(X, (deg - zero) * D2R));
  }

  _pose(cfg, s) {
    const z = cfg.zero;
    this._needle("Needle_Airspeed", dialDeg(cfg.airspeed, s.airspeed), z.airspeed);
    // The altimeter: the long hand once round a thousand (feet or metres),
    // the short hand once round ten thousand. Below the sea it reads zero.
    const alt = Math.max(0, s.alt);
    this._needle("Needle_Altimeter", ((alt % 1000) / 1000) * 360, 0);
    this._needle("Needle_Altimeter1k", ((alt % 10000) / 10000) * 360, 0);
    this._needle("Needle_Climb", dialDeg(cfg.climb === 1 ? VSI_MS : VSI_FPM, s.climb), 0);
    this._needle("Needle_Turn", dialDeg(TURN, s.turn), 0);
    const shake = this.shake ?? 0;
    const tach = dialDeg(cfg.tach, s.rpm + shake * 220);
    const boost = dialDeg(cfg.manifold, s.boost + shake * 0.08 * (cfg.engine.boostRated - cfg.engine.boostIdle));
    for (const suf of ["", "_L", "_R", "_1", "_2", "_3", "_4"]) {
      this._needle("Needle_Tach" + suf, tach, z.tach);
      this._needle("Needle_Manifold" + suf, boost, z.manifold);
    }
    // The horizon: the bar rolls against the bank and slides down as the
    // nose comes up, along its own rolled "up".
    const h = this.parts.get("Horizon");
    if (h) {
      const roll = -s.bank;
      h.node.quaternion.copy(h.q).multiply(_qa.setFromAxisAngle(X, roll));
      const k = -Math.max(-1, Math.min(1, s.pitch / HORIZON_PITCH)) * (h.travel ?? 0);
      h.node.position.set(h.p.x, h.p.y + Math.cos(roll) * k, h.p.z + Math.sin(roll) * k);
    }
    // The compass card holds north: a repeater's card turns anticlockwise
    // under its lubber mark as the nose swings right; the P8's flat card
    // turns about the vertical, the same way seen from above.
    const c = this.parts.get("Compass");
    if (c) {
      if (cfg.compassFlat) c.node.quaternion.copy(c.q).multiply(_qa.setFromAxisAngle(Y, s.heading));
      else c.node.quaternion.copy(c.q).multiply(_qa.setFromAxisAngle(X, -s.heading));
    }
    // The controls, where the pilot's hands and feet put them.
    const pc = Math.max(-1, Math.min(1, s.pitchCtl));
    const rc = Math.max(-1, Math.min(1, s.rollCtl));
    const yc = Math.max(-1, Math.min(1, s.yawCtl));
    const st = cfg.stick && this.parts.get(cfg.stick);
    if (st) {
      st.node.quaternion.copy(st.q)
        .multiply(_qa.setFromAxisAngle(Z, pc * STICK_PITCH))
        .multiply(_qb.setFromAxisAngle(X, rc * STICK_ROLL));
    }
    if (cfg.column) {
      for (const pre of cfg.copilot ? ["", "Co"] : [""]) {
        const col = this.parts.get(pre + cfg.column);
        if (col) col.node.quaternion.copy(col.q).multiply(_qa.setFromAxisAngle(Z, pc * COLUMN_PITCH));
        const wh = cfg.wheel && this.parts.get(pre + cfg.wheel);
        if (wh) wh.node.quaternion.copy(wh.q).multiply(_qa.setFromAxisAngle(X, rc * WHEEL_ROLL));
      }
    }
    // Left rudder (yaw +) puts the left pedal forward and the right one back.
    for (const pre of cfg.copilot ? ["", "Co"] : [""]) {
      for (const [side, sgn] of [["_L", 1], ["_R", -1]]) {
        const pd = this.parts.get(pre + "Pedal" + side);
        if (!pd) continue;
        if (cfg.pedalsTurn) pd.node.quaternion.copy(pd.q).multiply(_qa.setFromAxisAngle(Z, sgn * yc * PEDAL_SWING));
        else pd.node.position.set(pd.p.x + sgn * yc * PEDAL_SLIDE, pd.p.y, pd.p.z);
      }
    }
    // Throttles: forward travel is negative about Z, from the closed stop.
    const thr = -Math.max(0, Math.min(1, s.thr)) * THROTTLE_THROW;
    for (const suf of ["", "_L", "_R", "_1", "_2", "_3", "_4"]) {
      const t = this.parts.get("Throttle" + suf);
      if (t) t.node.quaternion.copy(t.q).multiply(_qa.setFromAxisAngle(Z, thr));
    }
    // The hood, eased off its stops at both ends of the run; a sliding hood
    // runs aft along -X, a hinged one swings about its rail.
    const hc = cfg.hood;
    const hood = hc && this.parts.get("Hood");
    if (hood) {
      const k = this.hood * this.hood * (3 - 2 * this.hood);
      if (hc.swing) hood.node.quaternion.copy(hood.q).multiply(_qa.setFromAxisAngle(X, k * hc.swing));
      else hood.node.position.set(hood.p.x - k * hc.slide, hood.p.y, hood.p.z);
      const crank = hc.crank && this.parts.get("Crank");
      if (crank) crank.node.quaternion.copy(crank.q).multiply(_qa.setFromAxisAngle(Z, k * hc.crank * 2 * Math.PI));
    }
  }
}
